import React, { useState } from 'react';
import styled from 'styled-components';
import Home from '../Home';

// --- Styled Components ---
const PageContainer = styled.div`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: #2d3748;
  line-height: 1.7;
  
  margin: 0 auto;
  padding: 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const HeaderSection = styled.header`
  text-align: center;
  padding: 80px 20px 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.1"><polygon points="1000,100 1000,0 0,100"/></svg>');
    background-size: cover;
  }
`;

const UnlockText = styled.h1`
  font-size: 3.2em;
  font-weight: 300;
  margin-bottom: 15px;
  position: relative;
  z-index: 2;
  
  span.seo-audit {
    font-weight: 800;
    background: linear-gradient(135deg, #f6e05e 0%, #faf089 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const FullPotential = styled.span`
  background: linear-gradient(135deg, #81e6d9 0%, #4fd1c7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  font-size: 1.1em;
  padding: 4px 12px;
  border-radius: 8px;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 12px;
    right: 12px;
    height: 2px;
    background: linear-gradient(135deg, #81e6d9 0%, #4fd1c7 100%);
    border-radius: 1px;
  }
`;

const SubHeaderText = styled.p`
  font-size: 1.3em;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
  font-weight: 300;
  letter-spacing: 0.5px;
`;

const ImageRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 50px 0 20px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const ImageCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  max-width: 280px;
  text-align: center;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
  
  .icon {
    font-size: 3em;
    margin-bottom: 15px;
    display: block;
  }
  
  .title {
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 10px;
    font-size: 1.1em;
  }
  
  .description {
    color: #718096;
    font-size: 0.95em;
    line-height: 1.5;
  }
`;

const GhostingTechSection = styled.section`
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: #fff;
  text-align: center;
  padding: 25px 20px;
  margin: 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    animation: float 20s infinite linear;
  }
  
  h2 {
    font-size: 1.8em;
    margin: 0;
    font-weight: 600;
    position: relative;
    z-index: 2;
    background: linear-gradient(135deg, #81e6d9 0%, #d6bcfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @keyframes float {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const StepList = styled.ul`
  list-style: none;
  padding: 60px 20px;
  max-width: 900px;
  margin: 0 auto;
  background: white;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;
  padding: 30px;
  border-radius: 16px;
  background: ${props => props.even ? '#f7fafc' : 'white'};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 6px solid ${props => props.color};
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 25px;
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid ${props => props.color};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5em;
  font-weight: 800;
  margin-right: 25px;
  flex-shrink: 0;
  background: ${props => props.color};
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
  }
`;

const StepContent = styled.p`
  text-align: left;
  margin: 0;
  padding-top: 8px;
  font-size: 1.15em;
  color: #4a5568;
  line-height: 1.7;
  
  @media (max-width: 768px) {
    text-align: center;
    padding-top: 0;
  }
`;

const ImportanceSection = styled.section`
  text-align: center;
  padding: 80px 20px;
  background: linear-gradient(135deg, #fed7d7 0%, #feebc8 100%);
  position: relative;
`;

const ImportanceTitle = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 20px 40px;
  border-bottom: 4px solid #ed8936;
  margin-bottom: 40px;
  display: inline-block;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2.2em;
    margin: 0;
    color: #2d3748;
    font-weight: 700;
  }
`;

const ImportancePills = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const Pill = styled.span`
  background: linear-gradient(135deg, #ed8936 0%, #f6ad55 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(237, 137, 54, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(237, 137, 54, 0.4);
    background: linear-gradient(135deg, #dd6b20 0%, #ed8936 100%);
  }
`;

const ImportanceQuoteBox = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 40px;
  border-radius: 20px;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  border: 2px solid rgba(237, 137, 54, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &::before {
    content: '"';
    font-size: 6em;
    color: #ed8936;
    position: absolute;
    top: -20px;
    left: 30px;
    opacity: 0.3;
    font-family: serif;
  }
  
  p {
    font-size: 1.25em;
    color: #4a5568;
    margin: 0;
    line-height: 1.7;
    font-style: italic;
    font-weight: 500;
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 80px 20px;
  text-align: center;
  color: white;
`;

const FAQTitle = styled.h2`
  font-size: 2.8em;
  margin-bottom: 50px;
  font-weight: 700;
  background: linear-gradient(135deg, #fff 0%, #e2e8f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItemWrapper = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  }
`;

const FAQItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: 600;
  color: #2d3748;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #f7fafc;
  }
  
  span:last-child {
    font-size: 1.5em;
    font-weight: 300;
    color: #667eea;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
`;

const FAQAnswer = styled.div`
  padding: ${props => props.isOpen ? '0 25px 25px' : '0 25px'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  color: #4a5568;
  line-height: 1.6;
  border-top: ${props => props.isOpen ? '1px solid #e2e8f0' : 'none'};
`;

const FooterSection = styled.footer`
  background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
  color: #fff;
  padding: 50px 20px 30px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const FollowUsText = styled.p`
  margin-bottom: 25px;
  color: #cbd5e0;
  font-size: 1.1em;
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.primary ? 
    'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' : 
    'linear-gradient(135deg, #718096 0%, #4a5568 100%)'};
  color: #fff;
  padding: 12px 25px;
  text-decoration: none;
  border-radius: 25px;
  margin: 0 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    color: white;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  padding: 18px 40px;
  font-size: 1.2em;
  border-radius: 50px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(72, 187, 120, 0.4);
  margin-top: 30px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(72, 187, 120, 0.6);
    background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  }
`;

// --- FAQ Data ---
const faqData = [
  {
    q: "What is an SEO audit, and why do I need one for my website?",
    a: "An SEO audit is a comprehensive evaluation of your website's search engine performance. You need one to identify technical errors, content gaps, and opportunities for improvement that will boost your rankings and traffic."
  },
  {
    q: "How long does it take to complete an SEO audit?",
    a: "The duration varies based on the size and complexity of your website. A smaller site might take 1-2 weeks, while a large e-commerce platform could take 3-4 weeks for thorough data collection and reporting."
  },
  {
    q: "How often should I get an SEO audit for my website?",
    a: "It's best practice to perform a full SEO audit annually. However, a mini-audit or health check should be done every quarter, especially after a major website migration or design change."
  },
  {
    q: "What common SEO issues can an audit uncover?",
    a: "Audits frequently uncover issues like broken links, slow page load speeds, duplicate content, poor mobile responsiveness, missing metadata, and incorrect robots.txt or sitemap settings."
  },
  {
    q: "What are the key components of an SEO audit?",
    a: "Key components include Technical SEO (site speed, crawling), On-Page SEO (keywords, content quality), Off-Page SEO (backlinks, authority), and User Experience (mobile-friendliness)."
  },
  {
    q: "What benefits can I expect from an SEO audit?",
    a: "You can expect increased organic traffic, higher search engine rankings, improved user experience, a lower bounce rate, and ultimately, a higher conversion rate for your business."
  },
  {
    q: "Can I conduct an SEO audit on my own, or should I hire a professional?",
    a: "While basic audits can be done using free tools, a professional is recommended for deep technical analysis, competitive research, and strategic advice on complex issues like core web vitals."
  },
  {
    q: "How can I get started with an SEO audit for my website?",
    a: "Start by checking your current Google Analytics and Search Console data. Then, contact a reliable provider like SRK Software to schedule a discovery call and define the scope of the full audit."
  }
];

// --- React Component ---
const SEOAudit = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const companyName = "SRK Software";

  const stepColors = ['#667eea', '#ed8936', '#48bb78', '#9f7aea', '#f56565'];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <Home>
      <PageContainer>
        <HeaderSection>
          <UnlockText>
            Unlock your Website's <FullPotential>full potential</FullPotential><br />
            with a comprehensive <span className="seo-audit">SEO audit</span>
          </UnlockText>
          <SubHeaderText>
            Boost your online visibility and performance with a hassle-free SEO audit.
          </SubHeaderText>
          <ImageRow>
            <ImageCard>
              <span className="icon">🔍</span>
              <div className="title">Deep Analysis</div>
              <div className="description">Comprehensive website scanning and technical assessment</div>
            </ImageCard>
            <ImageCard>
              <span className="icon">📊</span>
              <div className="title">Detailed Reporting</div>
              <div className="description">Actionable insights with clear performance metrics</div>
            </ImageCard>
            <ImageCard>
              <span className="icon">🚀</span>
              <div className="title">Performance Boost</div>
              <div className="description">Implement proven strategies for better rankings</div>
            </ImageCard>
          </ImageRow>
          <CTAButton>Get Your Free SEO Audit</CTAButton>
        </HeaderSection>

        <GhostingTechSection>
          <h2>Why Choose Advanced Technology for SEO Audit?</h2>
        </GhostingTechSection>

        <StepList>
          {[
            "Advanced technology is like having a digital detective for your website. It digs deep and finds the exact issues affecting your SEO, giving you the lowdown on what's going wrong.",
            "Say goodbye to guesswork! Our technology looks at everything from how your site's built to what's happening around it. It's like a full-body checkup for your website's health.",
            "With our advanced tools, you're in the loop at all times. It keeps an eye on things and lets you know when things change. You'll see improvements as they happen, keeping your site in top shape.",
            "Our technology provides the most unbiased analysis you can get. It checks your site without any preconceived notions or gut-based intuition that you may have.",
            "Not only does our technology point out problems, but it also gives you ideas on how to fix them. It's like having a tech-savvy buddy who knows just what tweaks will make your site shine online."
          ].map((content, index) => (
            <StepItem key={index} even={index % 2 === 0} color={stepColors[index]}>
              <StepNumber color={stepColors[index]}>{index + 1}</StepNumber>
              <StepContent>{content}</StepContent>
            </StepItem>
          ))}
        </StepList>

        <ImportanceSection>
          <ImportanceTitle>
            <h2>Why is an SEO Audit Important?</h2>
          </ImportanceTitle>
          <ImportancePills>
            <Pill>Be Seen by More People</Pill>
            <Pill>Make Your Website Super Fast</Pill>
            <Pill>Fix the Little Problems</Pill>
            <Pill>Be a Step Ahead of the Rest</Pill>
          </ImportancePills>
          <ImportanceQuoteBox>
            <p>An SEO audit is like giving your website a special makeover so it can stand out in the online crowd. It helps search engines find your site easily, so when people search for things related to what you do, they see you first!</p>
          </ImportanceQuoteBox>
        </ImportanceSection>

        <FAQSection>
          <FAQTitle>Frequently Asked Questions</FAQTitle>
          <FAQGrid>
            {faqData.map((item, index) => (
              <FAQItemWrapper key={index}>
                <FAQItemHeader 
                  onClick={() => toggleFAQ(index)}
                  isOpen={openFAQ === index}
                >
                  <span>{item.q}</span>
                  <span>+</span>
                </FAQItemHeader>
                <FAQAnswer isOpen={openFAQ === index}>
                  {item.a}
                </FAQAnswer>
              </FAQItemWrapper>
            ))}
          </FAQGrid>
        </FAQSection>

        <FooterSection>
          <FollowUsText>Stay connected with us on social media for the latest updates, insights, and industry news.</FollowUsText>
          <div>
            <Button href="#" primary>
              <span>📘</span> Facebook
            </Button>
            <Button href="#">
              <span>💼</span> LinkedIn
            </Button>
          </div>
         
        </FooterSection>
      </PageContainer>
    </Home>
  );
};

export default SEOAudit;